import React, { useState, useMemo } from "react";
import UniversalDataInspector from "../../../common/parsers/UniversalDataInspector";
import { UniversalLinkParser } from "../../../common/utils/UniversalLinkParser";
import { GenericFileUploader } from "../../../upload/services/genericFileUploader";

export default function WorkflowValidationView({
  selectedFile,
  results,
  isProcessing,
  onValidate
}) {
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [showJson, setShowJson] = useState(false);
  
  const [isPromoted, setIsPromoted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  // --- AUDIT CONTROLLER STATE ---
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const auditResults = useMemo(() => {
    if (!results?.esgTree?.fullPot) return null;

    let filtered = results.esgTree.fullPot;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        (n.business_chapter?.toLowerCase().includes(q)) ||
        (n.label?.toLowerCase().includes(q))
      );
    }

    const availableRoles = [...new Set(filtered.map(n => n.role_id))].sort();

    if (roleFilter) {
      filtered = filtered.filter(n => n.role_id === roleFilter);
    }

    const totalRows = filtered.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;
    const paginatedRows = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    return { paginatedRows, totalRows, totalPages, availableRoles };
  }, [results, searchQuery, roleFilter, currentPage]);

  if (!selectedFile) return (
    <div className="p-5 text-center text-muted small italic bg-light rounded border-dashed">
      Select an asset from the inventory to begin structural analysis.
    </div>
  );

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      {/* 🎯 HEADER: Clean Room Aesthetic (Blue text on White) */}
      <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-primary">2. Structural Analysis & Integrity Audit</h6>
        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-3 py-2 fw-bold">
          <i className="bi bi-shield-check me-1"></i> {selectedFile?.filename}
        </span>
      </div>

      <div className="card-body p-0">
        {!results ? (
          <div className="text-center py-5 bg-light">
            <button className="btn btn-primary fw-bold px-5 shadow-sm" onClick={onValidate} disabled={isProcessing}>
              {isProcessing ? "ANALYZING STRUCTURE..." : "START VALIDATION"}
            </button>
          </div>
        ) : (
          <div className="animate__animated animate__fadeIn">
            
            {/* 📋 WORKSHEET INSPECTOR */}
            <div className="worksheet-list border-bottom">
              {results.worksheets?.map((ws, i) => (
                <div key={i}>
                  <div
                    className={`d-flex align-items-center px-4 py-2 cursor-pointer hover-bg-light ${expandedSheet === ws.name ? "bg-light border-bottom" : ""}`}
                    onClick={() => setExpandedSheet(expandedSheet === ws.name ? null : ws.name)}
                  >
                    <div className="flex-grow-1 small fw-bold text-dark">
                        <i className={`bi bi-table me-2 ${ws.isLinkbase ? 'text-primary' : 'text-muted'}`}></i>
                        {ws.name}
                    </div>
                    <div className="text-muted small me-3">{ws.rowCount} Rows</div>
                    <i className={`bi bi-chevron-${expandedSheet === ws.name ? 'up' : 'down'} small text-muted`}></i>
                  </div>
                  {expandedSheet === ws.name && (
                    <div className="p-3 bg-white border-bottom animate__animated animate__fadeIn">
                      <UniversalDataInspector worksheet={ws} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 🎯 ESG TREE GENERATION GATE */}
            {!showJson && (
              <div className="p-4 bg-white text-center">
                <button className="btn btn-outline-primary fw-bold px-5" onClick={() => setShowJson(true)}>
                  GENERATE ESG TREE & AUDIT INTEGRITY
                </button>
              </div>
            )}

            {showJson && auditResults && (
              <div className="p-4 bg-light border-top animate__animated animate__fadeIn">
                
                {/* 🔍 SEARCH & FILTERS */}
                <div className="row g-3 mb-4">
                  <div className="col-md-7">
                    <label className="extra-small fw-bold text-muted text-uppercase mb-2">Search Taxonomy</label>
                    <input
                      type="text"
                      className="form-control shadow-none"
                      placeholder="Search Chapters or Labels..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="extra-small fw-bold text-muted text-uppercase mb-2">Filter Role</label>
                    <select
                      className="form-select fw-bold shadow-none"
                      value={roleFilter}
                      onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">All Roles ({auditResults?.availableRoles.length})</option>
                      {auditResults?.availableRoles.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>

                {/* 📋 TAXONOMY AUDIT TABLE (14px font for chapters) */}
                <div className="table-responsive border rounded bg-white shadow-sm mb-3" style={{ maxHeight: "500px" }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light extra-small text-uppercase fw-bold text-muted sticky-top">
                      <tr>
                        <th className="ps-3" style={{ width: "60px" }}>Level</th>
                        <th style={{ width: "100px" }}>Role ID</th>
                        <th>Taxonomy Definition & References</th>
                      </tr>
                    </thead>
                    <tbody className="small">
                      {auditResults?.paginatedRows.map((n, i) => (
                        <tr key={i}>
                          <td className="ps-3 fw-bold text-primary">L{n.level}</td>
                          <td className="text-muted small fw-bold">{n.role_id}</td>
                          <td className="py-3">
                            <div className="fw-bold text-dark mb-1" style={{ fontSize: "14px" }}>{n.business_chapter}</div>
                            <div className="text-secondary mb-2">{n.label}</div>
                            <div className="extra-small">{UniversalLinkParser(n.legal_reference)}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 🔢 STANDARD PAGINATION SUITE (Start/Finish) */}
                {auditResults?.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-2 rounded border shadow-sm">
                    <span className="small text-muted fw-bold ps-2">
                      Page {currentPage} of {auditResults.totalPages} 
                      <span className="mx-2">|</span> 
                      Total: {auditResults.totalRows}
                    </span>
                    <div className="btn-group">
                      <button className="btn btn-sm btn-outline-dark px-3" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>START</button>
                      <button className="btn btn-sm btn-outline-dark px-3" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>PREV</button>
                      <button className="btn btn-sm btn-primary px-4 disabled fw-bold text-white">{currentPage}</button>
                      <button className="btn btn-sm btn-outline-dark px-3" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === auditResults.totalPages}>NEXT</button>
                      <button className="btn btn-sm btn-outline-dark px-3" onClick={() => setCurrentPage(auditResults.totalPages)} disabled={currentPage === auditResults.totalPages}>FINISH</button>
                    </div>
                  </div>
                )}

                {/* 🚀 FINAL SAVE ACTION */}
                <div className="d-grid mt-4">
                  <button
                    className={`btn ${isPromoted ? "btn-secondary" : "btn-success"} btn-lg fw-bold shadow`}
                    onClick={async () => {
                      if (isSaving || isPromoted) return;
                      setIsSaving(true);
                      try {
                        const config = { 
                          bucketName: "esg-staging-clean", 
                          fileName: `ESG_Sync_${Date.now()}.json`, 
                          batchType: 1 
                        };
                        const res = await GenericFileUploader.uploadGeneratedData(results.esgTree.fullPot, config);
                        if (res?.success) { 
                          setIsPromoted(true); 
                          setSaveMessage("Workflow Concluded: Taxonomy data promoted to Clean Room."); 
                        }
                      } catch (err) { 
                        setSaveMessage("Save Failed: " + err.message); 
                      } finally { 
                        setIsSaving(false); 
                      }
                    }}
                    disabled={isSaving || isPromoted}
                  >
                    {isSaving ? "SAVING WORKFLOW..." : isPromoted ? "WORKFLOW CONCLUDED" : "SAVE & CONCLUDE WORKFLOW"}
                  </button>
                  {saveMessage && <p className="text-center small fw-bold mt-3 mb-0 text-primary animate__animated animate__pulse">{saveMessage}</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}